'use client';

import React, { useState } from 'react';
import { colors, baseStyles, formatNum } from './SharedUI';

// ==========================================
// 1. TẨY NÃO AI (System Prompt)
// ==========================================
const SYSTEM_PROMPT = `Bạn là một AI Quant Trader cấp cao. Nhiệm vụ của bạn là phân tích dữ liệu Order Flow (Sổ lệnh), Động lượng và Vĩ mô để đưa ra quyết định giao dịch đòn bẩy. Lệnh của bạn phải quản lý rủi ro tuyệt đối. Tuyệt đối KHÔNG hallucinate ra các mốc giá không có thật.

BẠN PHẢI TRẢ VỀ ĐÚNG ĐỊNH DẠNG JSON SAU, KHÔNG GIẢI THÍCH HAY BÌNH LUẬN GÌ THÊM (Chỉ trả về Object JSON thuần):
{
  "action": "LONG" hoặc "SHORT" hoặc "WAIT",
  "confidence": <số từ 0 đến 100>,
  "entry": <mốc giá vào lệnh (số) hoặc null>,
  "sl": <giá cắt lỗ (số) hoặc null>,
  "tp": <giá chốt lời (số) hoặc null>,
  "reasoning": "<1-2 câu giải thích ngắn gọn dựa trên dữ liệu>"
}`;

// Hàm làm sạch chuỗi JSON lỡ AI có bọc trong thẻ markdown ```json
const parseAIResponse = (text) => {
  try {
    let cleanText = text.replace(/```json/gi, '').replace(/```/g, '').trim();
    // Groq/Llama đôi khi trả về thêm text rác ở đầu/cuối, tìm vị trí ngoặc { } để cắt
    const firstBrace = cleanText.indexOf('{');
    const lastBrace = cleanText.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1) {
       cleanText = cleanText.substring(firstBrace, lastBrace + 1);
    }
    return JSON.parse(cleanText);
  } catch (error) {
    console.error("Lỗi Parse JSON:", text);
    throw new Error("AI trả về sai định dạng JSON");
  }
};

// ==========================================
// 2. CÁC HÀM FETCH GỌI TRỰC TIẾP TỪ BROWSER (FULL MIỄN PHÍ)
// ==========================================

// Thay thế OpenAI bằng GROQ (Dùng Llama 3 70B - Siêu nhanh, Miễn phí)
async function callGroq(prompt) {
  const apiKey = process.env.NEXT_PUBLIC_GROQ_API_KEY;
  if (!apiKey) throw new Error("Thiếu biến môi trường NEXT_PUBLIC_GROQ_API_KEY");

  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model: "llama-3.1-8b-instant", 
      response_format: { type: "json_object" }, 
      messages: [{ role: "system", content: SYSTEM_PROMPT }, { role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Groq Error: ${res.status} - ${await res.text()}`);
  const data = await res.json();
  return parseAIResponse(data.choices[0].message.content);
}

// Thay thế DeepSeek bằng OPENROUTER (Dùng model Free)
async function callOpenRouter(prompt) {
  const apiKey = process.env.NEXT_PUBLIC_OPENROUTER_API_KEY;
  if (!apiKey) throw new Error("Thiếu biến môi trường NEXT_PUBLIC_OPENROUTER_API_KEY");

  const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${apiKey}`,
      'HTTP-Referer': 'https://localhost:3000', // OpenRouter yêu cầu header này
      'X-Title': 'QuantBot'
    },
    body: JSON.stringify({
      model: "openrouter/free", // Chọn model Free của Llama 3
      response_format: { type: "json_object" },
      messages: [{ role: "system", content: SYSTEM_PROMPT }, { role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`OpenRouter Error: ${res.status} - ${await res.text()}`);
  const data = await res.json();
  return parseAIResponse(data.choices[0].message.content);
}

// Gemini vẫn giữ nguyên vì có gói Free 15 request/phút
async function callGemini(prompt) {
  const apiKey = process.env.NEXT_PUBLIC_GEMINI_API_KEY;
  if (!apiKey) throw new Error("Thiếu biến môi trường NEXT_PUBLIC_GEMINI_API_KEY");

  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      system_instruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      generationConfig: { responseMimeType: "application/json" }
    })
  });
  if (!res.ok) throw new Error(`Gemini Error: ${res.status} - ${await res.text()}`);
  const data = await res.json();
  return parseAIResponse(data.candidates[0].content.parts[0].text);
}


export default function AICouncilSection({ symbol = 'BTCUSDT', onTakeSnapshot }) {
  const [snapshot, setSnapshot] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  const takeSnapshot = () => {
    if (onTakeSnapshot) {
      const snapData = onTakeSnapshot();
      setSnapshot(snapData);
      setAiResults(null);
    }
  };

  const executeAI = async () => {
    setIsExecuting(true);
    setAiResults(null);

    const userPrompt = `Dữ liệu lúc ${snapshot.timestamp}:
- Giá Live: ${snapshot.macro.price} (${snapshot.macro.positionStr})
- Cản trên: ${snapshot.macro.sellZone} | Đỡ dưới: ${snapshot.macro.buyZone}
- Lệch sổ lệnh 100ms: Tường Đè/Đỡ = ${snapshot.micro.imbalance}x.
- Áp lực CVD Live: ${snapshot.micro.cvd} (Dương là Mua, Âm là Bán).
- Tiền Thật/Ảo (Bán): ${snapshot.micro.realAsks} / ${snapshot.micro.fakeAsks}.
- Tiền Thật/Ảo (Mua): ${snapshot.micro.realBids} / ${snapshot.micro.fakeBids}.
- Vị thế Cá Mập: Long trung bình = ${snapshot.smartMoney.longAvg}, Short trung bình = ${snapshot.smartMoney.shortAvg}.
- Quản lý vốn: Margin $${snapshot.wallet.margin}, Đòn bẩy x${snapshot.wallet.leverage}.
Dựa vào dữ liệu trên, hãy ra lệnh giao dịch.`;

    try {
      // 🚀 GỌI ĐỒNG THỜI 3 BỘ NÃO AI (HOÀN TOÀN MIỄN PHÍ)
      const [groqRes, routerRes, geminiRes] = await Promise.allSettled([
        callGroq(userPrompt),
        callOpenRouter(userPrompt),
        callGemini(userPrompt)
      ]);

      const fallbackJSON = { action: 'WAIT', confidence: 0, entry: null, sl: null, tp: null, reasoning: '🔴 Lỗi API hoặc Timeout. Bấm F12 xem Console.' };

      if (groqRes.status === 'rejected') console.error("⚠️ Groq (Llama) Xịt:", groqRes.reason);
      if (routerRes.status === 'rejected') console.error("⚠️ OpenRouter Xịt:", routerRes.reason);
      if (geminiRes.status === 'rejected') console.error("⚠️ Gemini Xịt:", geminiRes.reason);

      setAiResults({
        chatgpt: groqRes.status === 'fulfilled' ? groqRes.value : fallbackJSON,
        deepseek: routerRes.status === 'fulfilled' ? routerRes.value : fallbackJSON,
        gemini: geminiRes.status === 'fulfilled' ? geminiRes.value : fallbackJSON
      });

    } catch (error) {
      console.error("Lỗi kết nối tổng:", error);
      alert("🔴 Đã xảy ra lỗi hệ thống khi gọi AI!");
    } finally {
      setIsExecuting(false);
    }
  };

  const getConsensus = () => {
    if (!aiResults) return null;
    let shortCount = 0, longCount = 0, waitCount = 0;
    Object.values(aiResults).forEach(res => {
      if (res.action === 'SHORT') shortCount++;
      else if (res.action === 'LONG') longCount++;
      else waitCount++;
    });

    let decision = '⚖️ ĐỨNG NGOÀI (Chưa rõ xu hướng)';
    let color = colors.warn;
    if (shortCount >= 2) { decision = '🩸 ĐÁNH SHORT (Phe Bán Áp Đảo)'; color = colors.red; }
    if (longCount >= 2) { decision = '🚀 ĐÁNH LONG (Phe Mua Áp Đảo)'; color = colors.green; }
    if (waitCount >= 2 || (shortCount===1 && longCount===1 && waitCount===1)) { decision = '🚫 ĐỨNG NGOÀI (Nhiễu loạn)'; color = colors.textSub; }
    return { shortCount, longCount, waitCount, decision, color };
  };

  const consensus = getConsensus();

  return (
    <div style={{ ...baseStyles.card, marginBottom: '24px', border: `1px solid ${colors.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '16px' }}>
        <strong style={{ color: colors.warn, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>🧠</span> HỘI ĐỒNG AI CỐ VẤN ({symbol})
        </strong>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <button onClick={takeSnapshot} style={{ backgroundColor: 'transparent', border: `2px dashed #3b82f6`, color: '#3b82f6', padding: '12px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: !snapshot ? '0 0 15px rgba(59, 130, 246, 0.3)' : 'none', transition: 'all 0.2s' }}>
          📸 CHỤP SNAPSHOT LIỀN TAY (ĐÓNG BĂNG DỮ LIỆU LIVE)
        </button>
      </div>

      {snapshot && (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}`, marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: colors.textMain }}>📦 HỒ SƠ TÌNH BÁO (Dự kiến gửi cho AI)</span>
            <span style={{ fontSize: '12px', color: colors.textSub }}>Lấy lúc: {snapshot.timestamp}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', fontSize: '12px', color: colors.textSub }}>
            <div>
              <strong style={{ color: '#fff' }}>🌍 TỌA ĐỘ VĨ MÔ:</strong>
              <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Giá Live: <strong style={{ color: colors.warn }}>${formatNum(snapshot.macro.price, 4)}</strong></li>
                <li>Vị thế: {snapshot.macro.positionStr}</li>
                <li>Cản: {snapshot.macro.sellZone}</li>
                <li>Đỡ: {snapshot.macro.buyZone}</li>
              </ul>
            </div>
            <div>
              <strong style={{ color: '#fff' }}>🔬 MẶT TRẬN VI MÔ (100ms):</strong>
              <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Lệch Sổ Lệnh: {snapshot.micro.imbalance.toFixed(2)}x</li>
                <li>Ảo/Thật (Bán): {formatNum(snapshot.micro.fakeAsks)} / {formatNum(snapshot.micro.realAsks)}</li>
                <li>Ảo/Thật (Mua): {formatNum(snapshot.micro.fakeBids)} / {formatNum(snapshot.micro.realBids)}</li>
                <li>CVD Live: {snapshot.micro.cvd > 0 ? '+' : ''}{formatNum(snapshot.micro.cvd)}</li>
              </ul>
            </div>
            <div>
              <strong style={{ color: '#fff' }}>🐋 CÁ MẬP & QUẢN LÝ VỐN:</strong>
              <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                <li>Mập Long: ${formatNum(snapshot.smartMoney.longAvg, 4)}</li>
                <li>Mập Short: ${formatNum(snapshot.smartMoney.shortAvg, 4)}</li>
                <li>Vốn: ${formatNum(snapshot.wallet.margin)} | Đòn bẩy: x{snapshot.wallet.leverage}</li>
              </ul>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '16px', borderTop: `1px dashed ${colors.border}` }}>
            <div style={{ fontSize: '11px', color: colors.warn, marginBottom: '8px' }}>👇 Bác kiểm tra kỹ số liệu. Nếu chuẩn bài thì bấm gửi 👇</div>
            <button onClick={executeAI} disabled={isExecuting} style={{ backgroundColor: isExecuting ? colors.border : colors.warn, color: '#000', border: 'none', padding: '12px 32px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isExecuting ? 'not-allowed' : 'pointer' }}>
              {isExecuting ? '⏳ ĐANG CHỜ CÁC CỤ AI HỌP HỘI ĐỒNG...' : '🧠 DUYỆT! GỬI CHO HỘI ĐỒNG AI (MIỄN PHÍ)'}
            </button>
          </div>
        </div>
      )}

      {aiResults && consensus && (
         <div>
          <div style={{ backgroundColor: `${consensus.color}15`, border: `1px solid ${consensus.color}`, padding: '16px', borderRadius: '8px', textAlign: 'center', marginBottom: '20px' }}>
             <div style={{ fontSize: '13px', color: colors.textSub, marginBottom: '8px' }}>
                🏆 TỶ LỆ BẦU CHỌN: <strong style={{color: colors.red}}>[{consensus.shortCount} SHORT]</strong> vs <strong style={{color: colors.green}}>[{consensus.longCount} LONG]</strong> vs <strong style={{color: colors.warn}}>[{consensus.waitCount} WAIT]</strong>
             </div>
             <div style={{ fontSize: '20px', fontWeight: 'bold', color: consensus.color }}>QUYẾT ĐỊNH CHUNG: {consensus.decision}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {Object.entries(aiResults).map(([botName, data]) => {
              // Đổi lại nhãn tên cho oai, dù ruột là Groq và OpenRouter
              const nameMap = { chatgpt: '⚡ GROQ (Llama 3 70B)', deepseek: '🌐 OPENROUTER (Llama 8B)', gemini: '♊ GEMINI (1.5 Flash)' };
              const actionColor = data.action === 'LONG' ? colors.green : (data.action === 'SHORT' ? colors.red : colors.warn);
              return (
                <div key={botName} style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${colors.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '10px 16px', fontWeight: 'bold', fontSize: '13px', borderBottom: `1px solid ${colors.border}` }}>
                    {nameMap[botName]}
                  </div>
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: colors.textSub }}>CHỈ LỆNH:</span>
                      <strong style={{ color: actionColor }}>{data.action === 'LONG' ? '🚀' : (data.action === 'SHORT' ? '🩸' : '⚖️')} {data.action} ({data.confidence}%)</strong>
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '12px' }}>
                      <strong style={{ color: '#fff' }}>KẾ HOẠCH TÁC CHIẾN:</strong>
                      <ul style={{ paddingLeft: '16px', marginTop: '4px', marginBottom: '0' }}>
                        <li>Entry: {data.entry ? `$${formatNum(data.entry, 2)}` : 'Không có'}</li>
                        <li>SL: {data.sl ? `$${formatNum(data.sl, 2)}` : 'Không có'}</li>
                        <li>TP: {data.tp ? `$${formatNum(data.tp, 2)}` : 'Không có'}</li>
                      </ul>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textSub, lineHeight: '1.5' }}>
                      <strong style={{ color: '#fff', fontSize: '12px' }}>LÝ DO:</strong><br/>
                      <span style={{ fontStyle: 'italic' }}>"{data.reasoning}"</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
         </div>
      )}
    </div>
  );
}
